/**
 * ApprovalsPanel - the Approvals drawer (Story #5351 W1-T11, Screen Contract
 * G05: "Let an independent authorised reviewer evaluate a frozen plan,
 * consequence, evidence and expiry without losing context or accidental
 * drift.").
 *
 * No approvals backend exists anywhere in services/api.ts — verified: there
 * is no `/approvals` route of any kind. This panel therefore ships in two
 * honest modes rather than faking a live one:
 *
 *  - fixtureMode (opt-in, e.g. the /dev/fixtures state-fixtures route):
 *    demonstrates the full single-operator approval shape (DECISION #5284)
 *    end-to-end against illustrative data from ./fixtures.ts — review
 *    before/after, plan-hash display, expiry + changed-plan invalidation,
 *    a machine-verification step, and a FIXTURE-marked EvidenceReceipt kept
 *    only in this component's local state (never sent anywhere, never
 *    persisted).
 *  - live mode (the default): every row is still the same illustrative
 *    fixture content (there is nothing else to show — the mockup itself is
 *    marked "Illustrative data"), but there are NO action controls at all.
 *    Run verification and Approve were withdrawn from launch scope on
 *    2026-09-02 (see src/chrome/scopeDecisions.tsx) — they were
 *    `disabled={!fixtureMode}`, and the drawer mounts this panel with
 *    fixtureMode false, so in the real app they could never fire; a
 *    NotInLaunchScope statement replaces them. It makes NO network call of
 *    any kind — this file imports neither apiClient nor axios, so there is
 *    nothing here capable of making one.
 *
 * Single-operator shape (DECISION #5284, contracts/receipt.ts): the
 * requester and approver are the same operator. That is only a valid
 * approval when it is (1) a separate deliberate step from the request —
 * enforced here by a two-click flow (Run verification, then Approve), never
 * a single click, (2) bound to the exact plan hash — enforced by
 * deriveApprovalState blocking approve the instant currentPlanHash and
 * requestedPlanHash disagree, and (3) taken after independent machine
 * verification — enforced by requiring runFixtureVerification's result
 * before the Approve button enables.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are components/layout/*, outside this task's file ownership (same
 * handoff ScopeRibbon.tsx, HelpDrawer.tsx and JobsDrawer.tsx recorded).
 */
import React, { useState } from 'react';
import type { EvidenceReceipt } from '../../contracts/receipt';
import type { VerifierResult } from '../../contracts/verifier';
import UnavailableReason from '../ui/UnavailableReason';
import { NotInLaunchScope } from '../../chrome/scopeDecisions';
import {
  FIXTURE_APPROVALS, deriveApprovalState, runFixtureVerification, buildFixtureReceipt,
  type FixtureApproval, type ApprovalDecisionState, FIXTURE_SOURCE,
} from './fixtures';
import { isW1Shell } from '../../utils/w1Flag';

const STATE_LABEL: Record<ApprovalDecisionState, string> = {
  pending: 'Pending',
  expiring: 'Expiring',
  expired: 'Expired',
  invalidated: 'Invalidated',
  approved: 'Approved',
};

const STATE_BADGE: Record<ApprovalDecisionState, string> = {
  pending: 'bg-slate-100 text-slate-700',
  expiring: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-700',
  invalidated: 'bg-red-100 text-red-700',
  approved: 'bg-emerald-100 text-emerald-700',
};

export interface ApprovalsPanelProps {
  /** Demo mode only (e.g. /dev/fixtures) — never wired to a live decision. Default false. */
  fixtureMode?: boolean;
  /** Test/inspection hook: called with every FIXTURE receipt this panel produces. Never used to persist anything. */
  onFixtureReceipt?: (receipt: EvidenceReceipt & { source: typeof FIXTURE_SOURCE }) => void;
}

const ApprovalsPanel: React.FC<ApprovalsPanelProps> = ({ fixtureMode = false, onFixtureReceipt }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [verifications, setVerifications] = useState<Record<string, VerifierResult>>({});
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [blockedReasons, setBlockedReasons] = useState<Record<string, string>>({});

  if (!isW1Shell()) return null;

  const selected: FixtureApproval | undefined = FIXTURE_APPROVALS.find((a) => a.approvalId === selectedId);
  const selectedState: ApprovalDecisionState | undefined = selected
    ? (approvedIds.has(selected.approvalId) ? 'approved' : deriveApprovalState(selected))
    : undefined;

  const handleRunVerification = (approval: FixtureApproval) => {
    setVerifications((prev) => ({ ...prev, [approval.approvalId]: runFixtureVerification(approval) }));
  };

  const handleApprove = (approval: FixtureApproval) => {
    // Non-fixture mode never reaches here in the real UI — the Approve
    // button is not rendered at all below (2026-09-02 scope decision: a
    // control that can never fire is withdrawn, not greyed out).
    // This early return is a second, defence-in-depth guard: even a
    // synthetic click cannot make this branch touch the network, because
    // there is no apiClient/axios import anywhere in this file.
    if (!fixtureMode) return;
    const state = deriveApprovalState(approval);
    if (state === 'expired') {
      setBlockedReasons((prev) => ({
        ...prev,
        [approval.approvalId]: 'Expired approval cannot be reopened as actionable — request a new approval.',
      }));
      return;
    }
    if (state === 'invalidated') {
      setBlockedReasons((prev) => ({
        ...prev,
        [approval.approvalId]: 'Plan changed since this approval was requested — request a fresh review.',
      }));
      return;
    }
    const verification = verifications[approval.approvalId];
    if (!verification) {
      setBlockedReasons((prev) => ({
        ...prev,
        [approval.approvalId]: 'Run verification before approving — an approval is never taken without it.',
      }));
      return;
    }
    const receipt = buildFixtureReceipt(approval, approval.requester, verification);
    setApprovedIds((prev) => new Set(prev).add(approval.approvalId));
    setBlockedReasons((prev) => {
      const next = { ...prev };
      delete next[approval.approvalId];
      return next;
    });
    onFixtureReceipt?.(receipt);
  };

  return (
    <div data-testid="approvals-panel" className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900">Approvals</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Illustrative data{fixtureMode ? ' — fixture mode' : ''}.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-2">Approval</th>
              <th className="text-left px-4 py-2">Requester</th>
              <th className="text-left px-4 py-2">Expires</th>
              <th className="text-left px-4 py-2">Decision state</th>
            </tr>
          </thead>
          <tbody>
            {FIXTURE_APPROVALS.map((approval) => {
              const state = approvedIds.has(approval.approvalId) ? 'approved' : deriveApprovalState(approval);
              return (
                <tr
                  key={approval.approvalId}
                  onClick={() => setSelectedId(approval.approvalId)}
                  className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${selectedId === approval.approvalId ? 'bg-sky-50' : ''}`}
                >
                  <td className="px-4 py-2 font-mono text-xs">{approval.approvalId}</td>
                  <td className="px-4 py-2">{approval.requester}</td>
                  <td className="px-4 py-2 text-xs" title={approval.expiresAt}>
                    {new Date(approval.expiresAt).toLocaleTimeString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STATE_BADGE[state]}`}>
                      {STATE_LABEL[state]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {selected && selectedState && (
          <div className="px-4 py-4 border-t border-gray-200 space-y-3" data-testid="approval-review">
            <h3 className="text-sm font-semibold text-gray-900">Review — {selected.approvalId}</h3>

            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Plan hash</dt>
                <dd className="font-mono text-xs">{selected.currentPlanHash}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Target</dt>
                <dd className="text-right">{selected.target}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Before</dt>
                <dd className="text-gray-900">{selected.beforeSummary}</dd>
              </div>
              <div>
                <dt className="text-gray-500">After</dt>
                <dd className="text-gray-900">{selected.afterSummary}</dd>
              </div>
            </dl>

            {selectedState === 'invalidated' && (
              <UnavailableReason reason="Plan changed since this approval was requested (plan hash no longer matches) — request a fresh review." />
            )}
            {selectedState === 'expired' && (
              <UnavailableReason reason="This approval expired and cannot be reopened as actionable — request a new approval." />
            )}
            {selectedState === 'approved' && (
              <p className="text-sm text-emerald-700 font-medium">
                Approved (single-operator, fixture receipt — never persisted).
              </p>
            )}

            {fixtureMode && blockedReasons[selected.approvalId]
              && selectedState !== 'invalidated' && selectedState !== 'expired' && (
              <UnavailableReason reason={blockedReasons[selected.approvalId]} />
            )}

            {(selectedState === 'pending' || selectedState === 'expiring') && (
              fixtureMode ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleRunVerification(selected)}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Run verification (fixture)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApprove(selected)}
                    disabled={!verifications[selected.approvalId]}
                    className="px-3 py-1.5 text-sm rounded bg-iflow-700 text-white hover:bg-iflow-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Approve (single operator)
                  </button>
                </div>
              ) : (
                <NotInLaunchScope
                  control="Run verification and approve"
                  because="approvals run on fixture data only; there is no approval-plan store"
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalsPanel;
